import type { ProjectRecord } from "@repo/database/src/services/ProjectService";
import { ArrowDown, ArrowUp, Copy, FolderOpen, Pencil, Play, Plus, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { trpc } from "../../../lib/trpc";
import { getProjectCopy } from "../../data/projectCopy";
import { useTranslation } from "../../i18n";
import { Alert, Button, ButtonLink, Field, FieldLabel, Input, LoadingState, Modal, Select, Textarea } from "../ui";
import type { UploadRow } from "./types";
import "../../styles/projects.css";

type ProjectSummary = ProjectRecord & { itemCount: number };
type ProjectDraft = {
	title: string;
	description: string;
	visibility: "unlisted" | "private";
	slideDuration: number;
	uploadIds: string[];
	existing?: { id: string; revision: string };
};

export function ProjectLibrary({ uploads, loading }: { uploads: UploadRow[]; loading: boolean }) {
	const { locale } = useTranslation();
	const copy = getProjectCopy(locale);
	const utils = trpc.useUtils();
	const query = trpc.project.listMine.useQuery(undefined, { retry: false, networkMode: "always" });
	const [draft, setDraft] = useState<ProjectDraft | null>(null);
	const [original, setOriginal] = useState("");
	const [search, setSearch] = useState("");
	const [editingId, setEditingId] = useState<string | null>(null);
	const [deleting, setDeleting] = useState<ProjectSummary | null>(null);
	const [discard, setDiscard] = useState(false);
	const [notice, setNotice] = useState<{ tone: "success" | "error"; text: string } | null>(null);
	const [editFailed, setEditFailed] = useState(false);
	const save = trpc.project.save.useMutation({ networkMode: "always" });
	const remove = trpc.project.delete.useMutation({ networkMode: "always" });
	const dirty = draft !== null && JSON.stringify(draft) !== original;
	useEffect(() => {
		if (!dirty) return;
		const guard = (event: BeforeUnloadEvent) => {
			event.preventDefault();
			event.returnValue = "";
		};
		window.addEventListener("beforeunload", guard);
		return () => window.removeEventListener("beforeunload", guard);
	}, [dirty]);
	const openDraft = (value: ProjectDraft) => {
		save.reset();
		setDraft(value);
		setOriginal(JSON.stringify(value));
		setSearch("");
		setEditFailed(false);
	};
	const closeDraft = () => {
		if (dirty) setDiscard(true);
		else setDraft(null);
	};
	const edit = async (project: ProjectSummary) => {
		setEditingId(project.id);
		setEditFailed(false);
		try {
			const detail = await utils.client.project.getMine.query({ id: project.id });
			openDraft({
				title: detail.title,
				description: detail.description,
				visibility: detail.visibility,
				slideDuration: detail.slideDuration,
				uploadIds: detail.items.map(item => item.id),
				existing: { id: detail.id, revision: detail.revision },
			});
		} catch {
			setEditFailed(true);
		} finally {
			setEditingId(null);
		}
	};
	const persist = async () => {
		if (!draft || save.isLoading) return;
		try {
			await save.mutateAsync(draft);
			setDraft(null);
			setNotice({ tone: "success", text: copy.saved });
			await utils.project.listMine.invalidate();
		} catch {
			/* Keep edits available for retry. */
		}
	};
	const deleteProject = async () => {
		if (!deleting || remove.isLoading) return;
		try {
			await remove.mutateAsync({ id: deleting.id, revision: deleting.revision });
			setDeleting(null);
			setNotice({ tone: "success", text: copy.deleted });
			await utils.project.listMine.invalidate();
		} catch {
			await utils.project.listMine.invalidate();
		}
	};
	const copyLink = async (id: string) => {
		try {
			await navigator.clipboard.writeText(`${window.location.origin}/project/${id}`);
			setNotice({ tone: "success", text: copy.copied });
		} catch {
			setNotice({ tone: "error", text: copy.copyFailed });
		}
	};
	const move = (index: number, offset: number) => {
		if (!draft) return;
		const ids = [...draft.uploadIds];
		[ids[index], ids[index + offset]] = [ids[index + offset], ids[index]];
		setDraft({ ...draft, uploadIds: ids });
	};
	const available = uploads.filter(upload =>
		`${upload.title ?? ""} ${upload.modelType ?? ""} ${upload.shareId}`.toLowerCase().includes(search.toLowerCase()),
	);
	return (
		<section className="project-library" aria-labelledby="projects-heading">
			<header className="project-library-heading">
				<div>
					<div className="project-heading-title">
						<FolderOpen size={20} />
						<h2 id="projects-heading">{copy.title}</h2>
						<span className="account-count">{query.data?.length ?? "—"}</span>
					</div>
					<p>{copy.description}</p>
				</div>
				<Button
					size="sm"
					disabled={loading || !!editingId}
					onClick={() =>
						openDraft({ title: "", description: "", visibility: "unlisted", slideDuration: 15, uploadIds: [] })
					}
				>
					<Plus size={16} />
					{copy.create}
				</Button>
			</header>
			{notice && (
				<Alert tone={notice.tone} aria-live="polite">
					{notice.text}
				</Alert>
			)}
			{editFailed && <Alert tone="error">{copy.failed}</Alert>}
			{query.isLoading ? (
				<LoadingState label={copy.loading} />
			) : query.error ? (
				<div className="space-y-3">
					<Alert tone="error">{copy.failed}</Alert>
					<Button variant="secondary" onClick={() => void query.refetch()}>
						{copy.retry}
					</Button>
				</div>
			) : !query.data?.length ? (
				<div className="project-empty">
					<FolderOpen size={28} />
					<div>
						<h3>{copy.emptyTitle}</h3>
						<p>{copy.emptyBody}</p>
					</div>
				</div>
			) : (
				<ul className="project-grid">
					{query.data.map(project => (
						<li className="project-card" key={project.id}>
							<div className="project-card-cover" aria-hidden="true">
								<FolderOpen size={30} />
								<span>{copy.items(project.itemCount)}</span>
							</div>
							<div className="project-card-body">
								<h3>{project.title}</h3>
								{project.description && <p>{project.description}</p>}
								<span className="project-visibility">
									{project.visibility === "private" ? copy.private : copy.unlisted}
								</span>
								<ButtonLink href={`/project/${project.id}`} variant="secondary" size="sm">
									<Play size={14} />
									{copy.open}
								</ButtonLink>
								<div className="project-card-actions">
									<Button size="sm" variant="ghost" disabled={!!editingId} onClick={() => void edit(project)}>
										<Pencil size={14} />
										{copy.edit}
									</Button>
									<Button
										variant="ghost"
										size="icon"
										aria-label={`${project.title}: ${copy.copy}`}
										onClick={() => void copyLink(project.id)}
									>
										<Copy size={15} />
									</Button>
									<Button
										variant="ghost"
										size="icon"
										aria-label={`${project.title}: ${copy.delete}`}
										onClick={() => {
											remove.reset();
											setDeleting(project);
										}}
									>
										<Trash2 size={15} />
									</Button>
								</div>
							</div>
						</li>
					))}
				</ul>
			)}
			<Modal
				open={draft !== null}
				onOpenChange={open => {
					if (!open) closeDraft();
				}}
				title={draft?.existing ? copy.editTitle : copy.create}
				closeLabel={copy.close}
				busy={save.isLoading}
				className="account-surface project-editor"
				footer={
					<>
						<Button variant="secondary" disabled={save.isLoading} onClick={closeDraft}>
							{copy.cancel}
						</Button>
						<Button
							disabled={
								save.isLoading ||
								!draft?.title.trim() ||
								!Number.isInteger(draft.slideDuration) ||
								draft.slideDuration < 5 ||
								draft.slideDuration > 120
							}
							onClick={() => void persist()}
						>
							{save.isLoading ? copy.saving : copy.save}
						</Button>
					</>
				}
			>
				{draft && (
					<>
						{save.error && (
							<Alert tone="error">{save.error.data?.code === "CONFLICT" ? copy.conflict : copy.saveFailed}</Alert>
						)}
						<Field>
							<FieldLabel htmlFor="project-title">{copy.name}</FieldLabel>
							<Input
								id="project-title"
								value={draft.title}
								maxLength={200}
								placeholder={copy.namePlaceholder}
								disabled={save.isLoading}
								onChange={event => setDraft({ ...draft, title: event.target.value })}
							/>
						</Field>
						<Field>
							<FieldLabel htmlFor="project-description">{copy.about}</FieldLabel>
							<Textarea
								id="project-description"
								value={draft.description}
								maxLength={3000}
								rows={2}
								placeholder={copy.aboutPlaceholder}
								disabled={save.isLoading}
								onChange={event => setDraft({ ...draft, description: event.target.value })}
							/>
						</Field>
						<div className="project-editor-settings">
							<Field>
								<FieldLabel htmlFor="project-visibility">{copy.visibility}</FieldLabel>
								<Select
									id="project-visibility"
									value={draft.visibility}
									disabled={save.isLoading}
									onChange={event =>
										setDraft({ ...draft, visibility: event.target.value as ProjectDraft["visibility"] })
									}
								>
									<option value="unlisted">{copy.unlisted}</option>
									<option value="private">{copy.private}</option>
								</Select>
							</Field>
							<Field>
								<FieldLabel htmlFor="project-duration">
									{copy.duration} ({copy.seconds})
								</FieldLabel>
								<Input
									id="project-duration"
									type="number"
									min={5}
									max={120}
									value={Number.isNaN(draft.slideDuration) ? "" : draft.slideDuration}
									disabled={save.isLoading}
									onChange={event => setDraft({ ...draft, slideDuration: event.target.valueAsNumber })}
								/>
							</Field>
						</div>
						<p className="project-hint">{copy.accessHint}</p>
						<div className="project-model-picker">
							<div>
								<h3>{copy.models}</h3>
								<Input
									aria-label={copy.search}
									placeholder={copy.search}
									value={search}
									onChange={event => setSearch(event.target.value)}
								/>
								<div className="project-picker-list">
									{available.length ? (
										available.map(upload => (
											<label key={upload.id} className="project-picker-model">
												<input
													type="checkbox"
													checked={draft.uploadIds.includes(upload.id)}
													disabled={
														save.isLoading || (!draft.uploadIds.includes(upload.id) && draft.uploadIds.length >= 50)
													}
													onChange={event =>
														setDraft({
															...draft,
															uploadIds: event.target.checked
																? [...draft.uploadIds, upload.id]
																: draft.uploadIds.filter(id => id !== upload.id),
														})
													}
												/>
												<span>
													{upload.title || copy.untitled}
													<small>
														{upload.modelType?.toUpperCase()} ·{" "}
														{upload.visibility === "private" ? copy.private : upload.shareId}
													</small>
												</span>
											</label>
										))
									) : (
										<p className="project-hint">{uploads.length ? copy.noResults : copy.noModels}</p>
									)}
								</div>
							</div>
							<div>
								<h3>
									{copy.selected} <span className="account-count">{draft.uploadIds.length} / 50</span>
								</h3>
								<p className="project-hint">{copy.selectionHint}</p>
								<ol className="project-order-list">
									{draft.uploadIds.map((id, index) => {
										const upload = uploads.find(item => item.id === id);
										return (
											<li key={id}>
												<span className="project-order-number">{index + 1}</span>
												<span className="project-order-name">{upload?.title || copy.untitled}</span>
												<div>
													<Button
														size="icon"
														variant="ghost"
														aria-label={`${upload?.title || copy.untitled}: ${copy.up}`}
														disabled={save.isLoading || index === 0}
														onClick={() => move(index, -1)}
													>
														<ArrowUp size={14} />
													</Button>
													<Button
														size="icon"
														variant="ghost"
														aria-label={`${upload?.title || copy.untitled}: ${copy.down}`}
														disabled={save.isLoading || index === draft.uploadIds.length - 1}
														onClick={() => move(index, 1)}
													>
														<ArrowDown size={14} />
													</Button>
													<Button
														size="icon"
														variant="ghost"
														aria-label={`${upload?.title || copy.untitled}: ${copy.remove}`}
														disabled={save.isLoading}
														onClick={() =>
															setDraft({ ...draft, uploadIds: draft.uploadIds.filter(value => value !== id) })
														}
													>
														<X size={14} />
													</Button>
												</div>
											</li>
										);
									})}
								</ol>
								{!draft.uploadIds.length && <p className="project-hint">{copy.noSelection}</p>}
							</div>
						</div>
					</>
				)}
			</Modal>
			<Modal
				open={discard}
				onOpenChange={setDiscard}
				title={copy.discardTitle}
				description={copy.discardBody}
				closeLabel={copy.close}
				footer={
					<>
						<Button variant="secondary" onClick={() => setDiscard(false)}>
							{copy.keepEditing}
						</Button>
						<Button
							variant="destructive"
							onClick={() => {
								setDiscard(false);
								setDraft(null);
							}}
						>
							{copy.discard}
						</Button>
					</>
				}
			/>
			<Modal
				open={!!deleting}
				onOpenChange={open => {
					if (!open) setDeleting(null);
				}}
				title={copy.deleteTitle}
				description={copy.deleteBody}
				closeLabel={copy.close}
				busy={remove.isLoading}
				footer={
					<>
						<Button variant="secondary" disabled={remove.isLoading} onClick={() => setDeleting(null)}>
							{copy.cancel}
						</Button>
						<Button variant="destructive" disabled={remove.isLoading} onClick={() => void deleteProject()}>
							{remove.isLoading ? copy.deleting : copy.delete}
						</Button>
					</>
				}
			>
				<p>{deleting?.title}</p>
				{remove.error && <Alert tone="error">{copy.deleteFailed}</Alert>}
			</Modal>
		</section>
	);
}
